'use client'

import React from 'react'
import LandingLayout from '@components/Landing/LandingLayout'
import routes from '@routes'

const Page = () => {
  return (
    <LandingLayout
      title="Terms of service"
      limited
      meta={
        <>
          <title>Terms of service - Enhanci</title>
        </>
      }
    >
      <h1>Enhanci Terms of Service</h1>
      <p>
        <strong>Effective Date:</strong> 1 November 2025
      </p>

      <p>
        Welcome to <strong>Enhanci.com</strong> (“Enhanci”, “we”, “our”, or “us”).
        <br />
        These Terms of Service (“Terms”) govern your access to and use of the Enhanci
        website, tools, and related services (collectively, the “Services”). By using our
        Services, you agree to these Terms. If you do not agree, you must not use the
        Services.
      </p>

      <h2>1. Description of Service</h2>
      <p>
        Enhanci provides an online tool that allows users to design and embed customizable
        pricing table widgets on their own websites (the “Widgets”). Enhanci does not host
        or control any website on which a Widget is installed and is not responsible for
        any effects the Widget may have on your website’s functionality, appearance, or
        performance.
      </p>
      <p>
        At present, the Services are provided <strong>free of charge</strong>. We may
        introduce paid features or subscriptions in the future, and will provide users
        with reasonable notice and an opportunity to review and accept updated terms
        before any charges apply.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 16 years old to use the Services. By using Enhanci, you
        confirm that you meet this requirement and have the authority to agree to these
        Terms.
      </p>

      <h2>3. Accounts and Security</h2>
      <p>
        To use certain features, you may be required to create an account and provide an
        email address and password.
        <br />
        You agree to:
        <br />
        – Provide accurate, current information during registration,
        <br />
        – Keep your password confidential and secure,
        <br />– Notify us immediately of any unauthorized access to your account.
      </p>
      <p>
        We store all passwords in <strong>hashed and salted form</strong>, never in plain
        text. However, no method of transmission or storage is completely secure, and we
        cannot guarantee absolute security.
        <br />
        You are responsible for all activity under your account.
      </p>

      <h2>4. Use of Services</h2>
      <p>
        You agree to use the Services only for lawful purposes and in accordance with
        these Terms. You may not:
        <br />
        – Use the Services for any illegal, fraudulent, or harmful purpose,
        <br />
        – Interfere with or disrupt the operation of the Services,
        <br />
        – Attempt to gain unauthorized access to our systems or data,
        <br />– Use the Services to distribute malicious code or scripts.
      </p>
      <p>
        You are solely responsible for ensuring that embedding any Enhanci Widget on your
        website:
        <br />
        – Complies with your website’s own terms of use and privacy policy,
        <br />
        – Does not violate applicable laws, and
        <br />– Does not create security or performance issues for your site.
      </p>
      <p>
        Enhanci is not responsible for the operation, security, or compliance of your
        website or hosting environment.
      </p>

      <h2>5. Ownership and Intellectual Property</h2>
      <p>
        All intellectual property rights in the Services and Widgets (including software,
        design, and documentation) are owned by Enhanci.
        <br />
        We grant you a{' '}
        <strong>limited, non-exclusive, non-transferable, revocable license</strong> to
        use the Services and embed Widgets on your website, subject to these Terms.
      </p>
      <p>
        You retain ownership of your website and its content. You grant Enhanci permission
        to host, display, and deliver your Widget code as necessary to provide the
        Services.
      </p>

      <h2>6. Termination</h2>
      <p>
        You may stop using the Services at any time. You can delete your account through
        your user dashboard or by contacting us.
        <br />
        We may suspend or terminate your access if we believe you have violated these
        Terms or used the Services in a way that could cause harm or liability.
        <br />
        Upon termination, your right to use the Services and Widgets will cease
        immediately, and we may delete your data after a reasonable period.
      </p>

      <h2>7. Disclaimer of Warranties</h2>
      <p>
        The Services are provided <strong>“as is” and “as available”</strong>, without
        warranties of any kind, whether express or implied, including but not limited to
        implied warranties of merchantability, fitness for a particular purpose, or
        non-infringement.
      </p>
      <p>
        Enhanci makes no guarantee that:
        <br />
        – The Services will be uninterrupted, error-free, or secure,
        <br />
        – The Widgets will function correctly on all websites or browsers,
        <br />– Any defects or issues will be corrected.
      </p>
      <p>
        You acknowledge that Enhanci Widgets are designed to be installed on third-party
        websites, and we are not responsible for any damage, performance issues, or
        conflicts that may result from embedding a Widget or script on your site.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Enhanci shall not be liable for any
        indirect, incidental, consequential, special, or punitive damages, or for any loss
        of profits, revenue, data, or goodwill arising from your use of the Services.
      </p>
      <p>
        Without limiting the foregoing, Enhanci shall not be liable for any loss or damage
        arising from:
        <br />
        – Service downtime or unavailability,
        <br />
        – Errors or omissions in the generated Widget code,
        <br />– Interactions between the Widget and other software or content on your
        site.
      </p>

      <h2>9. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless Enhanci and its affiliates,
        employees, and agents from and against any claims, damages, losses, liabilities,
        or expenses arising out of:
        <br />
        – Your use or misuse of the Services,
        <br />
        – Any breach of these Terms,
        <br />– Your website’s content or the embedding of any Widget thereon.
      </p>

      <h2>10. Privacy</h2>
      <p>
        Your privacy is important to us. Our handling of personal data is described in our{' '}
        <a href={routes.publicRoutes.privacyPolicy}>Privacy Policy</a>, which forms part
        of these Terms.
        <br />
        By using the Services, you consent to the collection and use of your information
        as described in that policy.
      </p>

      <h2>11. Beta and Free Access</h2>
      <p>
        The Services are currently offered on a <strong>free and trial basis</strong>.
        <br />
        You acknowledge that Enhanci has no obligation to maintain, support, or continue
        providing the Services, and may modify or discontinue them at any time without
        notice or liability to you.
      </p>

      <h2>12. Modifications to Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will post the updated
        version with a new “Effective Date” above. Continued use of the Services after
        changes are posted constitutes your acceptance of the revised Terms.
      </p>

      <h2>13. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of{' '}
        <strong>England and Wales</strong>. Any disputes arising under or in connection
        with these Terms shall be subject to the{' '}
        <strong>exclusive jurisdiction of the courts of England and Wales</strong>.
      </p>

      <h2>14. Contact</h2>
      <p>
        If you have any questions about these Terms, please contact us at:
        <br />
        <strong>inbox@enhanci.com</strong>
      </p>
    </LandingLayout>
  )
}

export default Page
